/**
 * Mine tekster and Rapport columns on a patient report.
 * Same preference shape as Filer and Markering: open until this user closes
 * the column, and a width remembered per user. Nothing here is patient data.
 */

export const REPORT_TOOLS_MIN_WIDTH = 1280;
export const REPORT_TOOL_WIDTH_MIN = 280;
/** Today's Mine tekster column (18rem). */
export const REPORT_SOURCES_WIDTH_DEFAULT = 288;
/** Today's Rapport column (20rem). */
export const REPORT_DOCUMENT_WIDTH_DEFAULT = 320;
/** Closed rail card. The grid gap is counted separately. */
export const REPORT_RAIL_WIDTH = 48;
export const REPORT_GRID_GAP = 16;
/** The preview keeps this much room, same idea as the file document. */
export const REPORT_PREVIEW_MIN = 320;

export type ReportToolColumn = 'sources' | 'document';
export type ReportToolPresence = 'open' | 'rail' | 'hidden';

export function reportToolStorageKey(
  userId: string,
  column: ReportToolColumn,
  kind: 'open' | 'width',
): string {
  return kind === 'open'
    ? `clinio-report-${column}-open:${userId}`
    : `clinio-report-${column}-width:${userId}`;
}

function defaultWidth(column: ReportToolColumn): number {
  return column === 'sources' ? REPORT_SOURCES_WIDTH_DEFAULT : REPORT_DOCUMENT_WIDTH_DEFAULT;
}

export function readReportToolOpen(
  userId: string | null | undefined,
  column: ReportToolColumn,
): boolean {
  if (!userId || typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem(reportToolStorageKey(userId, column, 'open'));
    if (raw === '0') return false;
    if (raw === '1') return true;
    return true;
  } catch {
    return true;
  }
}

export function writeReportToolOpen(
  userId: string | null | undefined,
  column: ReportToolColumn,
  open: boolean,
): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(reportToolStorageKey(userId, column, 'open'), open ? '1' : '0');
  } catch {
    /* ignore quota / private mode */
  }
}

export function readReportToolWidth(
  userId: string | null | undefined,
  column: ReportToolColumn,
): number {
  const fallback = defaultWidth(column);
  if (!userId || typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(reportToolStorageKey(userId, column, 'width'));
    const parsed = raw == null ? NaN : Number(raw);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.max(REPORT_TOOL_WIDTH_MIN, Math.round(parsed));
  } catch {
    return fallback;
  }
}

export function writeReportToolWidth(
  userId: string | null | undefined,
  column: ReportToolColumn,
  width: number,
): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      reportToolStorageKey(userId, column, 'width'),
      String(Math.max(REPORT_TOOL_WIDTH_MIN, Math.round(width))),
    );
  } catch {
    /* ignore quota / private mode */
  }
}

export function reportToolOccupied(presence: ReportToolPresence, width: number): number {
  if (presence === 'hidden') return 0;
  if (presence === 'rail') return REPORT_RAIL_WIDTH;
  return width;
}

/**
 * Largest width this column may use while the other column, the gaps, and
 * the preview still fit. The floor is the column's own default, so a tight
 * window does not paint a column wider than today.
 */
export function maxReportToolWidth(
  workspaceWidth: number,
  otherOccupied: number,
  floor: number,
): number {
  const gaps = REPORT_GRID_GAP * 2;
  const half = Math.floor(workspaceWidth * 0.5);
  const room = Math.floor(workspaceWidth - otherOccupied - gaps - REPORT_PREVIEW_MIN);
  return Math.max(floor, Math.min(half, room));
}

export function clampReportToolWidth(
  width: number,
  workspaceWidth: number,
  otherOccupied: number,
  floor: number,
): number {
  const max = maxReportToolWidth(workspaceWidth, otherOccupied, floor);
  return Math.min(max, Math.max(REPORT_TOOL_WIDTH_MIN, Math.round(width)));
}

/**
 * Widths actually painted. A saved preference can be wider than the current
 * workspace; the other preference is kept so it comes back when there is room.
 */
export function displayedReportToolWidths(input: {
  sourcesPref: number;
  documentPref: number;
  workspaceWidth: number;
  sources: ReportToolPresence;
  document: ReportToolPresence;
}): { sources: number; document: number } {
  const documentOccupied = (width: number) => reportToolOccupied(input.document, width);
  const sourcesOccupied = (width: number) => reportToolOccupied(input.sources, width);
  let sources =
    input.sources === 'open'
      ? clampReportToolWidth(
          input.sourcesPref,
          input.workspaceWidth,
          documentOccupied(input.documentPref),
          REPORT_SOURCES_WIDTH_DEFAULT,
        )
      : input.sourcesPref;
  const document =
    input.document === 'open'
      ? clampReportToolWidth(
          input.documentPref,
          input.workspaceWidth,
          sourcesOccupied(sources),
          REPORT_DOCUMENT_WIDTH_DEFAULT,
        )
      : input.documentPref;
  if (input.sources === 'open') {
    sources = clampReportToolWidth(
      input.sourcesPref,
      input.workspaceWidth,
      documentOccupied(document),
      REPORT_SOURCES_WIDTH_DEFAULT,
    );
  }
  return { sources, document };
}
